/* eslint-disable @typescript-eslint/explicit-member-accessibility */
import { INoEditService } from '@src/slide/application/no-edit/service/no-edit-service/no-edit-service.interface';
import { ScriptLoader } from '@src/slide/application/service/edit-extension/edit-extension-export';
import { Command } from '@tencent/docs-connection';
import {
    AuthProps,
    AuthType,
    IAuth,
    SAFE_FOLDER_MOVE_EVENT,
} from '@src/slide/application/no-edit/plugins/auth/base/auth.interface';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import { OnlineEditServices } from '@src/slide/domain/slide-app/scenes/online/core/online-edit-services.core';
import { IDocInfoManager } from '@src/slide/domain/slide-app/core/interfaces/doc-info.interface';
import _isEqual from 'lodash/isEqual';
import _pick from 'lodash/pick';
import { IEventEmitter } from '@src/slide/infrastructure/module/independent/event-emitter/event-emitter.interface';
import { EventEmitter as EventBus } from '@src/slide/infrastructure/module/independent/event-emitter';
import { AuthEventsKeys, AuthEventsMap } from '@src/slide/application/no-edit/plugins/auth/base/auth-events.interface';
import type { IPrivilegeAttribute } from '@src/slide/domain/slide-app/core/interfaces/client-vars.interface';
import { IBaseEditorExtensionService } from '@src/slide/application/service/edit-extension/core/editor/base/interface';
import userLocalStorage, { LocalStorageKeys } from '@src/slide/infrastructure/module/independent/local-storage';
import i18n from '@i18next/i18n';
import ua from '@tencent/docs-user-agent';

export default class Auth implements IAuth {
    expireTimeId;

    private _event: IEventEmitter<AuthEventsMap>;

    private _policy: AuthType = AuthType.Self;
    private _memberList: Array<any> = [];

    private advpolicy: any = {};
    private showWxTips: boolean;

    // 有效期相关
    private _exptime: number;
    private _remaintime: number;
    private _settime: number;
    private _starttime: number;

    private noEditService?: INoEditService;
    private editExtensionService?: IBaseEditorExtensionService;

    public get policy(): AuthType {
        return this._policy;
    }

    public get memberList(): Array<any> {
        return this._memberList;
    }

    public get info(): any {
        return {
            policy: this._policy,
            memlist: this._memberList,
        };
    }

    public get exptime(): number {
        return this._exptime;
    }

    public get remaintime(): number {
        return this._remaintime;
    }

    public get settime(): number {
        return this._settime;
    }

    public get starttime(): number {
        return this._starttime;
    }

    public get event(): IEventEmitter<AuthEventsMap> {
        return this._event;
    }

    public get privilegeAttribute() {
        return SlideDataServices.PrivilegeManager()?.getPrivilegeAttribute();
    }

    public constructor(props: AuthProps) {
        this.noEditService = props.noEditService;
        this.editExtensionService = props.editExtensionService;

        this._event = new EventBus();
        this.editExtensionService?.onReady(() => {
            SlideDataServices.DocInfoManager.promise().then((docInfo) => {
                docInfo.waitForReady(() => this.init(docInfo));
            });
        });

        SlideDataServices.SDKDataServices.EventManager.promise().then(({ socketEvents }) => {
            socketEvents.onRightMessage((data) => {
                this.handleRightMessage(data);
                this.checkPrivilege();
                this.handleSafaFolderMove(data);
            });
        });

        window.addEventListener('online', () => this.handleOnline());
    }

    init(docInfo: IDocInfoManager) {
        if (docInfo.isOwner) {
            const p1 = new Promise((resolve) => {
                this._event.on(AuthEventsKeys.AUTH_CHANGE, () => {
                    resolve(null);
                });
            });

            const p2 = new Promise((resolve) => {
                this._event.on(AuthEventsKeys.AUTH_EXP_TIME_CHANGE, () => {
                    resolve(null);
                });
            });

            Promise.all([p1, p2]).then(() => {
                this._event.emit(AuthEventsKeys.AUTH_MANAGER_READY);
            });
        } else {
            const p2 = new Promise((resolve) => {
                this._event.on(AuthEventsKeys.AUTH_EXP_TIME_CHANGE, () => {
                    resolve(null);
                });
            });

            Promise.all([p2]).then(() => {
                this._event.emit(AuthEventsKeys.AUTH_MANAGER_READY);
            });
        }

        this._policy = docInfo.policy ?? -1;
        this.getAuthInfo();
        if (this.shouldFetchAuthExpTime()) {
            this.getAuthExpTime();
        }
    }

    /**
     * 判断链接是否已经过期
     */
    isAuthExpired() {
        return !!((this.settime !== undefined && this.settime !== 0 && this.remaintime === 0));
    }

    /**
     * 是否为永久有效
     */
    isAuthExpTimeForverValid() {
        return !!((this.settime !== undefined && this.settime === 0));
    }

    notifyAuthUpdate4RightMsg(data: any, isOwner: boolean) {
        let handled = false;
        // 后台发出的权限变更通知，没有内容，也要刷新
        if (data && (data.payload === '' || data.payload === null)) {
            if (isOwner) {
                // 通知权限模块权限更新
                ScriptLoader?.dispatch('TX-AUTH-UPDATED');
                // 重新拉取权限信息
                this.getAuthInfo();
            }
            handled = true;
        }
        return handled;
    }

    notifyPriChanged4RightMsg(editable: boolean, advpolicy: number) {
        // 如果不能编辑(只读)，并且高级权限有修改，则需要用户刷新
        if (!editable && advpolicy === 1) {
            this._event.emit(AuthEventsKeys.AUTH_PRIVILEGE_CHANGE);
        }
    }

    setAuthExpInfo4RightMsg(exptime: number, settime: number, starttime: number) {
        if (exptime !== undefined && settime !== undefined && starttime !== undefined) {
            const newSettime = exptime - starttime;
            if (newSettime > 0) {
                this.setAuthExpInfo(
                    exptime,
                    newSettime,
                    newSettime,
                    starttime,
                );
            }
        }
    }

    notifyAuthModified4RightMsg(msgFilterData: any, seq: any, isOwner: boolean) {
        if (msgFilterData) {
            const tempMsgFilterData = msgFilterData;
            if (tempMsgFilterData[seq] === undefined) {
                if (isOwner) {
                    // 权限修改成功通知
                    this._event.emit(AuthEventsKeys.AUTH_MODIFIED);
                    // 通知权限模块权限更新
                    ScriptLoader?.dispatch('TX-AUTH-UPDATED');
                    // 重新拉取权限信息
                    this.getAuthInfo();
                }
            } else {
                delete tempMsgFilterData[seq];
            }
        }
    }

    /**
     * 处理socket
     * @param data
     */
    handleRightMessage(data: any) {
        try {
            const isOwner = !!SlideDataServices.DocInfoManager()?.isOwner;
            if (this.notifyAuthUpdate4RightMsg(data, isOwner)) {
                return;
            }

            const msgFilterData = JSON.parse(decodeURIComponent(data.payload));
            if (msgFilterData === null) {
                return;
            }
            const { seq } = msgFilterData;
            if (msgFilterData.action === 1) {
                this.notifyAuthModified4RightMsg(
                    SlideDataServices.ClientVarsService()?.getClientVars().msgFilterData,
                    seq,
                    isOwner,
                );
                const editable = !!SlideDataServices.PrivilegeManager()?.editable;
                this.notifyPriChanged4RightMsg(editable, msgFilterData.advpolicy);
            } else if (msgFilterData.action === 2) {
                // 目前action均为1，此处预留
            } else {
                // 权限有效期发生改变
                ScriptLoader?.dispatch('ADVPER_AUTH_EXPIRED');
                this.setAuthExpInfo4RightMsg(msgFilterData.exptime, msgFilterData.settime, msgFilterData.starttime);
            }
        } catch (err) {
            console.error(`handleRightMessage:${err}`);
        }
    }

    checkPrivilege() {
        if (!this.isCheckPrivilege()) return;
        this.comparePrivilege((result) => {
            const oldPrivilege = this.privilegeAttribute || {};
            if (result?.response?.onpost) {
                const newPrivilege = result.response.onpost.privilege_attribute?.docx_attribute || {};
                // 权限变更
                if (this.isPrivilegeChange(oldPrivilege, newPrivilege)) {
                    // 文档权限发生变化，刷新
                    this._event.emit(AuthEventsKeys.AUTH_PRIVILEGE_CHANGE, { canEdit: newPrivilege.can_edit });
                }
            }
        });
    }

    isCheckPrivilege() {
        if (SlideDataServices.DocInfoManager()?.isOwner) return false;
        return true; // 非自己创建的文档，补拉权限的具体信息
    }

    isPrivilegeChange(oldPrivilege, newPrivilege): boolean {
        const pickedCommonKeys: Array<keyof IPrivilegeAttribute> = ['can_read', 'can_edit', 'can_copy', 'can_print', 'can_copy_doc', 'can_export', 'can_apply_edit', 'can_save_template', 'can_copy_out'];
        return !_isEqual(
            _pick(oldPrivilege, pickedCommonKeys),
            _pick(newPrivilege, pickedCommonKeys),
        );
    }

    /**
     * 设置过期时间timer
     */
    setAuthExpTimer() {
        if (this.isAuthExpTimeForverValid()) {
            console.log('adv: is vaild forever');
            return;
        }

        // 文档有效期过期
        if (this.isAuthExpired()) {
            this.handleAuthExpTimeInvalid();
        } else {
            this.handleAuthExpTimeValid();
        }
    }

    /**
     * 处理过期 ，直接弹框
     */
    handleAuthExpTimeInvalid() {
        console.log('adv: is invaild');
        // 文档过期处理
        this._event.emit(AuthEventsKeys.AUTH_EXPIRED);
    }

    /**
     * 处理未过期 , 通过本地计时器实现
     */
    handleAuthExpTimeValid() {
        // 设置文档过期提醒定时器
        let timeoutInterval = this.remaintime;
        if (!SlideDataServices.DocInfoManager()?.isOwner && timeoutInterval > 5) { // 协作者文档过期这里提前一点
            timeoutInterval = timeoutInterval - 3;
        }
        timeoutInterval = timeoutInterval * 1000;

        // 清除旧定时器
        if (this.expireTimeId) {
            console.log('adv: clear expireTimeId');
            clearTimeout(this.expireTimeId);
            this.expireTimeId = null;
        }
        this.expireTimeId = setTimeout(() => {
            this.expireTimeId = null;
            console.log('adv: expire from timer, creator');
            // 从新拉取过期信息，同步服务器
            this.getAuthExpTime();
        }, timeoutInterval);
        console.log(`adv: settimeout, timeoutInterval:${timeoutInterval}`);
    }

    /**
     * 判断是否有并设置有效期过期标志
     * @param {*} padId
     */
    isAndSetAuthExpReaded(padId) {
        if (!padId || !localStorage) {
            return false;
        }
        let exptimeReadedSet = userLocalStorage.getItem(LocalStorageKeys.EXPTIME_READED_SET);
        const fg = ';';
        if (!exptimeReadedSet) {
            exptimeReadedSet = padId;
            userLocalStorage.setItem(LocalStorageKeys.EXPTIME_READED_SET, exptimeReadedSet + fg);
            return false;
        } if (exptimeReadedSet.indexOf(padId + fg) == -1) {
            const n = (exptimeReadedSet.split(fg)).length - 1;
            if (n > 20) { // 只存20个
                const firstf = exptimeReadedSet.indexOf(fg);
                exptimeReadedSet = exptimeReadedSet.substring(firstf + 1, exptimeReadedSet.length);
            }
            exptimeReadedSet = exptimeReadedSet + padId + fg;
            userLocalStorage.setItem(LocalStorageKeys.EXPTIME_READED_SET, exptimeReadedSet);
            return false;
        }
        return true;
    }

    public setAuthInfo(info) {
        this._policy = info.policy as AuthType;
        this._memberList = info.memlist;
        this._event.emit(AuthEventsKeys.AUTH_CHANGE);
    }

    public setAuthExpInfo(exptime, remaintime, settime, starttime) {
        this._exptime = exptime;
        this._remaintime = remaintime;
        this._settime = settime;
        this._starttime = starttime;
        this._event.emit(AuthEventsKeys.AUTH_EXP_TIME_CHANGE, this.isAuthExpired());
        this.setAuthExpTimer();
    }

    /**
     * 拉取权限信息
     */
    public async getAuthInfo() {
        const { isOwner, padId = '', creatorDomain = '' } = await SlideDataServices.DocInfoManager.promise();
        const { getAuthInfo } = await OnlineEditServices.OnlineApiService.promise();
        // 只有文档所有者才能获取权限信息
        if (isOwner) {
            getAuthInfo(padId, creatorDomain)
                .then((res) => {
                    if (res.data.cgicode === 0 && res.data.retcode === 0) {
                        const info = res.data;
                        this.setAuthInfo(info);
                    } else {
                        console.error(`getAuthInfo res.cgicode !== 0 || res.cgicode !== 0${res}`);
                    }
                })
                .catch((err) => {
                    console.error(`getAuthInfo err: ${JSON.stringify(err)}`);
                });
        }
    }

    /**
     * 拉取有效期信息
     */
    public async getAuthExpTime() {
        const { domainId = '', padId = '' } = await SlideDataServices.DocInfoManager.promise();
        const { getAuthExpTime } = await OnlineEditServices.OnlineApiService.promise();
        getAuthExpTime(domainId, padId)
            .then((res) => {
                if (res.data.cgicode === 0 && res.data.retcode === 0) {
                    let { exptime, remaintime, settime, starttime } = res.data;

                    // 对 B/C 端接口返回值类型差异先做一版兼容
                    exptime = Number(exptime);
                    remaintime = Number(remaintime);
                    settime = Number(settime);
                    starttime = Number(starttime);

                    this.setAuthExpInfo(exptime, remaintime, settime, starttime);
                    console.log(`getAuthExpTime exptime:${this._exptime} remaintime:${this._remaintime} settime:${this._settime} starttime:${this._starttime}`);
                } else {
                    console.error(`handleAuthExpTime res.cgicode !== 0 || res.cgicode !== 0${res}`);
                }
            })
            .catch((err) => {
                console.error(`getAuthExpTime error: ${JSON.stringify(err)}`);
            });
    }

    protected shouldFetchAuthExpTime(): boolean {
        return true;
    }

    private handleOnline() {
        this.editExtensionService?.onReady(() => {
            this.getAuthInfo();
            if (this.shouldFetchAuthExpTime()) {
                this.getAuthExpTime();
            }
            this.checkPrivilege();
        });
    }


    /**
     * socket拉取权限信息
     * @param callback
     */
    private comparePrivilege(callback?: (res) => void) {
        const socketInstance = OnlineEditServices.CollaborationManager()?.getSocket();
        socketInstance?.sendMessage({
            cmd: 'GET_PRIVILEGE' as Command,
            taskId: 0,
            data: JSON.stringify({}),
            customParam: void 0,
            onError: (seq: number, cmd: string, data: string, result: any) => {
                // 失败不做提示处理 nothing
                callback?.(null);
            },
            onsend: (seq: number, cmd: string, data: string, result: any) => {
                if (result.errCode === 0
                    && result.response
                    && result.response.onpost
                    && result.response.onpost.result === 0) { // 成功回包
                    callback?.(result);
                } else { // 失败不做提示处理 nothing
                    callback?.(null);
                }
            },
        });
    }

    private handleSafaFolderMove(data) {
        if (data?.action === SAFE_FOLDER_MOVE_EVENT.MOVE_TO_SAFE
            || data?.action === SAFE_FOLDER_MOVE_EVENT.SAFE_TO_PRIVATE) {
            // 加密文件夹不管是移入还是移出都需要通知用户刷新
            this._event.emit(AuthEventsKeys.AUTH_PRIVILEGE_CHANGE, { tipsMessage: i18n.t('加密文档状态已发生变化') });
            // 如果在端里需要通知端刷一下状态
            if (ua.isTencentDocsApp && ua.isTencentDocsAppVersionAboveOrEqual('3.9.5') && window.mqq) {
                window.mqq.invoke('docx', 'refreshDocInfo');
            }
        }
    }
}
